import {
    LEDGER_MINUS,
    NEGATIVE_BALANCE_ANNOTATION,
    NEGATIVE_BALANCE_CLASS,
} from '../types/constants';

/** Rounds credit amounts to the nearest 0.1 for display. */
export function roundCredits(n: number): number {
    if (!Number.isFinite(n)) return 0;
    return Math.round(n * 10) / 10;
}

/** @deprecated Use roundCredits — activity ledger uses the same 0.1 rounding. */
export function roundActivityCredits(n: number): number {
    return roundCredits(n);
}

/** Formats a credit amount rounded to the nearest 0.1 (shows one decimal when needed). */
export function formatCredits(n: number): string {
    const rounded = roundCredits(n);
    const hasFraction = rounded % 1 !== 0;
    return rounded.toLocaleString(undefined, {
        minimumFractionDigits: hasFraction ? 1 : 0,
        maximumFractionDigits: 1,
    });
}

/** @deprecated Use formatCredits — activity ledger uses the same 0.1 rounding. */
export function formatActivityCredits(n: number): string {
    return formatCredits(n);
}

/** Like formatCredits but with a unit suffix (default " cr"). */
export function formatCreditsWithUnit(n: number, unit = ' cr'): string {
    return `${formatCredits(n)}${unit}`;
}

/** Absolute credit amount with the ledger U+2212 glyph when negative. */
export function formatSignedCredits(n: number): string {
    const body = formatCredits(Math.abs(n));
    return n < 0 ? `${LEDGER_MINUS}${body}` : body;
}

export function formatSignedCreditsWithUnit(n: number, unit = ' cr'): string {
    return `${formatSignedCredits(n)}${unit}`;
}

export type RemainingCreditsPresentation = {
    amount: string;
    className: string;
    annotation: string | null;
    isNegative: boolean;
};

/**
 * One remaining presentation for the four credit surfaces.
 * Envelope math may still clamp; this formats the raw remaining the UI shows.
 */
export function presentRemainingCredits(
    n: number,
    nonNegativeClassName = 'text-default-500',
    usagePct?: number | null,
): RemainingCreditsPresentation {
    const isNegative = n < 0 || (usagePct != null && usagePct > 1);
    return {
        amount: formatSignedCreditsWithUnit(n),
        className: isNegative ? NEGATIVE_BALANCE_CLASS : nonNegativeClassName,
        annotation: isNegative ? NEGATIVE_BALANCE_ANNOTATION : null,
        isNegative,
    };
}

/** Fleet remaining colour: remaining sign wins over percent-only bands. */
export function remainingToneClass(
    remaining: number | null,
    pct: number | null,
    warnThresholdPct: number,
    dangerPct = 0.95,
): string {
    if ((remaining != null && remaining < 0) || (pct != null && pct > 1)) {
        return `${NEGATIVE_BALANCE_CLASS} font-semibold`;
    }
    if (pct == null) return 'text-default-700';
    if (pct >= dangerPct) return 'text-danger-600 font-semibold';
    if (pct >= warnThresholdPct) return 'text-warning-600 font-semibold';
    return 'text-default-700';
}

/** Rounds dollar amounts to the nearest cent. */
export function roundDollars(n: number): number {
    if (!Number.isFinite(n)) return 0;
    return Math.round(n * 100) / 100;
}

/** Formats a dollar amount rounded to the nearest cent. */
export function formatDollars(n: number): string {
    return roundDollars(n).toLocaleString('en-US', {
        style: 'currency',
        currency: 'USD',
        minimumFractionDigits: 2,
        maximumFractionDigits: 2,
    });
}

/** @deprecated Use formatCreditsWithUnit — activity ledger uses the same 0.1 rounding. */
export function formatActivityCreditsWithUnit(n: number, unit = ' cr'): string {
    return formatCreditsWithUnit(n, unit);
}
